import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Wrench,
  Search,
  BookMarked,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  School2,
  Loader2,
  Trash2,
  Pencil,
  X,
  BarChart3,
  Filter,
  SlidersHorizontal,
  Save,
  Database,
  CheckCircle2,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MoneyInput from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ServicesItems() {
  const [q, setQ] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [sort, setSort] = useState("recent");
  const [minVal, setMinVal] = useState("");
  const [maxVal, setMaxVal] = useState("");
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState({ count: 0, min: 0, max: 0, avg: 0 });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // { id, description, value, note }
  const [savingEdit, setSavingEdit] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/schools", { params: { size: 500 } });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setSchools(arr);
      } catch { /* ignore */ }
    })();
  }, []);

  const buildParams = () => {
    const p = { sort, size: 100 };
    if (q.trim()) p.q = q.trim();
    if (schoolId) p.school_id = schoolId;
    if (minVal !== "" && !isNaN(Number(minVal))) p.min_value = Number(minVal);
    if (maxVal !== "" && !isNaN(Number(maxVal))) p.max_value = Number(maxVal);
    return p;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const statsParams = {};
      if (params.q) statsParams.q = params.q;
      const [list, st] = await Promise.all([
        api.get("/services-catalog", { params }),
        api.get("/services-catalog/stats", { params: statsParams }),
      ]);
      setItems(list.data?.items || []);
      setTotal(list.data?.total || 0);
      setStats(st.data || { count: 0, min: 0, max: 0, avg: 0 });
    } catch (e) {
      toast.error("Falha ao carregar catálogo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, schoolId, sort, minVal, maxVal]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/services-catalog/${toDelete.id}`);
      toast.success("Entrada removida");
      setToDelete(null);
      fetchAll();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const openEdit = (entry) => {
    setEditEntry({
      id: entry.id,
      description: entry.description || "",
      min_value: Number(entry.min_value ?? entry.value ?? 0),
      max_value: Number(entry.max_value ?? entry.value ?? 0),
      note: entry.note || "",
    });
  };

  const saveEdit = async () => {
    if (!editEntry) return;
    const desc = (editEntry.description || "").trim();
    const mn = Number(editEntry.min_value || 0);
    const mx = Number(editEntry.max_value || 0);
    if (!desc) { toast.error("Preencha a descrição"); return; }
    if (!mn || mn <= 0) { toast.error("Informe o valor mínimo"); return; }
    if (!mx || mx <= 0) { toast.error("Informe o valor máximo"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/services-catalog/${editEntry.id}`, {
        description: desc,
        min_value: mn,
        max_value: mx,
        note: editEntry.note || "",
      });
      toast.success("Alterações salvas");
      setEditEntry(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSavingEdit(false);
    }
  };

  const runImport = async () => {
    if (!window.confirm("Gerar catálogo CURADO a partir dos orçamentos?\n\nIsso limpa os registros importados antigos e cria ~12 categorias simples (Lâmpadas, Elétrica, Pintura, Desentupimento, etc.) com valor MEDIANO calculado dos seus orçamentos reais (outliers são removidos automaticamente).\n\nPode rodar quantas vezes quiser.")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const r = await api.post("/services-catalog/curate-from-budgets");
      setImportResult(r.data);
      toast.success(`${r.data?.categories_created || 0} categorias criadas`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na curadoria");
    } finally {
      setImporting(false);
    }
  };

  const clearFilters = () => {
    setQ("");
    setSchoolId("");
    setSort("recent");
    setMinVal("");
    setMaxVal("");
  };

  const hasAnyFilter = useMemo(() => !!(q || schoolId || minVal || maxVal), [q, schoolId, minVal, maxVal]);

  return (
    <div className="space-y-6 pb-24" data-testid="services-catalog-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shrink-0">
          <BookMarked className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">Serviços & Itens</h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Base histórica de preços. Toda vez que você salvar um item no catálogo, ele fica disponível aqui para pesquisa.
          </p>
        </div>
        <Button
          onClick={runImport}
          disabled={importing}
          variant="outline"
          className="shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          data-testid="btn-import-from-budgets"
          title="Gerar catálogo curado a partir dos orçamentos reais"
        >
          {importing ? (
            <><Loader2 className="w-4 h-4 sm:mr-1.5 animate-spin" /> <span className="hidden sm:inline">Gerando...</span></>
          ) : (
            <><Database className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Gerar catálogo curado</span></>
          )}
        </Button>
      </div>

      {importResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3" data-testid="import-result">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-xs sm:text-sm text-emerald-900">
            <p className="font-semibold">Catálogo curado gerado</p>
            <p className="mt-0.5 text-emerald-800">
              <b>{importResult.categories_created || 0}</b> categorias criadas a partir de <b>{importResult.total_items_analyzed || 0}</b> itens analisados dos orçamentos reais. Outliers removidos automaticamente.
            </p>
          </div>
          <button onClick={() => setImportResult(null)} className="text-emerald-500 hover:text-emerald-800 p-1" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Insights */}
      {stats.count > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 card-shadow">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> Registros
            </p>
            <p className="text-2xl font-black text-slate-900 mt-1 tabular-nums">{stats.count}</p>
            {hasAnyFilter && <p className="text-[10px] text-slate-400 mt-0.5">dos filtros ativos</p>}
          </div>
          <div className="bg-white rounded-2xl border border-emerald-200 p-4 card-shadow">
            <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Mínimo
            </p>
            <p className="text-lg font-black text-emerald-700 mt-1 tabular-nums">{BRL(stats.min)}</p>
          </div>
          <div className="bg-white rounded-2xl border border-violet-200 p-4 card-shadow">
            <p className="text-[10px] text-violet-600 font-bold uppercase tracking-wider flex items-center gap-1">
              <Minus className="w-3 h-3" /> Média
            </p>
            <p className="text-lg font-black text-violet-700 mt-1 tabular-nums">{BRL(stats.avg)}</p>
          </div>
          <div className="bg-white rounded-2xl border border-rose-200 p-4 card-shadow">
            <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Máximo
            </p>
            <p className="text-lg font-black text-rose-700 mt-1 tabular-nums">{BRL(stats.max)}</p>
          </div>
        </div>
      ) : null}

      {/* Search bar */}
      <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-3 sm:p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por descrição... (ex: lâmpada, pintura, telha)"
              className="pl-9 pr-9 h-10"
              data-testid="input-search"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Limpar">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            className="h-10 shrink-0 border-slate-200"
            data-testid="btn-toggle-filters"
          >
            <SlidersHorizontal className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Filtros</span>
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-100" data-testid="filters-panel">
            <Select value={schoolId || "__all__"} onValueChange={(v) => setSchoolId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="filter-school"><SelectValue placeholder="Escola" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as escolas</SelectItem>
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 text-xs" data-testid="filter-sort"><SelectValue placeholder="Ordenar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Mais recentes</SelectItem>
                <SelectItem value="value_desc">Maior valor</SelectItem>
                <SelectItem value="value_asc">Menor valor</SelectItem>
                <SelectItem value="usage">Mais reusados</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={minVal}
              onChange={(e) => setMinVal(e.target.value)}
              placeholder="Valor mín R$"
              className="h-9 text-xs"
              data-testid="filter-min"
            />
            <Input
              type="number"
              value={maxVal}
              onChange={(e) => setMaxVal(e.target.value)}
              placeholder="Valor máx R$"
              className="h-9 text-xs"
              data-testid="filter-max"
            />
            {hasAnyFilter && (
              <Button onClick={clearFilters} variant="ghost" className="col-span-2 sm:col-span-4 h-8 text-xs text-slate-500" data-testid="btn-clear-filters">
                <X className="w-3 h-3 mr-1" /> Limpar filtros
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-12 text-center">
          <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-7 h-7 text-slate-400" />
          </div>
          <h2 className="font-heading text-lg font-bold text-slate-900">Catálogo vazio</h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto text-sm">
            {hasAnyFilter
              ? "Nenhum registro encontrado para os filtros aplicados."
              : "Ao criar itens de orçamentos, use o botão \"Salvar no catálogo\" para começar a montar sua base histórica de preços."}
          </p>
          {hasAnyFilter && (
            <Button onClick={clearFilters} variant="outline" className="mt-4" data-testid="btn-empty-clear">
              <X className="w-3 h-3 mr-1" /> Limpar filtros
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="catalog-list">
          <p className="text-xs text-slate-500">{total} registro{total !== 1 ? "s" : ""}</p>
          {items.map((it) => (
            <div
              key={it.id}
              className="bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md rounded-xl p-3 sm:p-4 transition-all"
              data-testid={`catalog-row-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base font-semibold text-slate-900">{it.description}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] sm:text-xs text-slate-500">
                    {it.school_name && (
                      <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                        <School2 className="w-3 h-3" /> {it.school_name}
                      </span>
                    )}
                    {it.budget_number && (
                      <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                        Orç. #{it.budget_number}
                      </span>
                    )}
                    {it.created_at && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(it.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                    {it.times_used > 0 && (
                      <span className="text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 font-semibold">
                        Reusado {it.times_used}x
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right flex flex-col items-end gap-1">
                  {it.min_value != null && it.max_value != null && it.min_value !== it.max_value ? (
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Faixa</p>
                      <p className="text-sm sm:text-base font-black text-indigo-700 tabular-nums whitespace-nowrap leading-tight">
                        {BRL(it.min_value)} <span className="text-slate-400 font-normal">—</span> {BRL(it.max_value)}
                      </p>
                      <p className="text-[10px] text-slate-500 tabular-nums">
                        méd. {BRL((Number(it.min_value) + Number(it.max_value)) / 2)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-lg sm:text-xl font-black text-indigo-700 tabular-nums whitespace-nowrap">{BRL(it.value)}</p>
                  )}
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => openEdit(it)}
                      className="text-slate-300 hover:text-indigo-600 p-1"
                      aria-label="Editar"
                      data-testid={`btn-edit-${it.id}`}
                      title="Editar descrição e valor"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setToDelete(it)}
                      className="text-slate-300 hover:text-rose-600 p-1"
                      aria-label="Remover"
                      data-testid={`btn-delete-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit entry */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent data-testid="edit-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" /> Editar entrada do catálogo
            </DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Descrição *</Label>
                <Textarea
                  value={editEntry.description}
                  onChange={(e) => setEditEntry((v) => ({ ...v, description: e.target.value }))}
                  rows={2}
                  data-testid="input-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-emerald-700">Valor mínimo (R$) *</Label>
                  <MoneyInput
                    value={editEntry.min_value}
                    onChange={(v) => setEditEntry((e) => ({ ...e, min_value: v }))}
                    data-testid="input-edit-min"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-rose-700">Valor máximo (R$) *</Label>
                  <MoneyInput
                    value={editEntry.max_value}
                    onChange={(v) => setEditEntry((e) => ({ ...e, max_value: v }))}
                    data-testid="input-edit-max"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 -mt-2">
                Dica: ex. "Troca de lâmpadas" entre <b>R$ 15</b> e <b>R$ 85</b>. A escola vai ver essa faixa e a média automaticamente.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Observação (opcional)</Label>
                <Textarea
                  value={editEntry.note || ""}
                  onChange={(e) => setEditEntry((v) => ({ ...v, note: e.target.value }))}
                  rows={2}
                  placeholder="Ex.: preço por unidade, exclui material, altura até 3m, etc."
                  data-testid="input-edit-note"
                />
                <p className="text-[10px] text-slate-500">
                  Esta observação aparece pra escola na consulta.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancelar</Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-save-edit"
            >
              {savingEdit ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvando...</> : <><Save className="w-4 h-4 mr-1.5" /> Salvar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover do catálogo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta entrada será removida da base histórica. Essa ação não afeta o orçamento de origem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
